import React, { useEffect, useMemo, useRef } from "react";
import { Chart, registerables } from "chart.js";
import styles from "./HealingTrajectoryChart.module.scss";

Chart.register(...registerables);

const mcsdBandPlugin = {
  id: "mcsdBand",
  beforeDatasetsDraw(chart, _args, opts) {
    const { bandYMin, bandYMax } = opts;
    if (bandYMin == null || bandYMax == null) return;

    const {
      ctx,
      chartArea: { left, right },
      scales,
    } = chart;
    const yS = scales.y;

    const pxTop = yS.getPixelForValue(Math.max(bandYMin, bandYMax));
    const pxBottom = yS.getPixelForValue(Math.min(bandYMin, bandYMax));

    ctx.save();

    ctx.fillStyle = "rgba(230, 168, 23, 0.06)";
    ctx.fillRect(left, pxTop, right - left, pxBottom - pxTop);

    ctx.beginPath();
    ctx.moveTo(left, pxTop);
    ctx.lineTo(right, pxTop);
    ctx.strokeStyle = "rgba(230, 168, 23, 0.4)";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(left, pxBottom);
    ctx.lineTo(right, pxBottom);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.restore();
  },
};

const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx, scales } = chart;
    const pts = opts.pts || [];

    ctx.save();
    pts.forEach((pt) => {
      const px = scales.x.getPixelForValue(pt.x);
      const py = scales.y.getPixelForValue(pt.y);
      ctx.font = "600 11px 'Segoe UI', sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "#333";
      ctx.fillText(Number(pt.y).toFixed(1), px, py - 14);
    });
    ctx.restore();
  },
};

const HealingTrajectoryChart = ({ data: dataProp, trendInfo, mcsdAnalysis, surfaceAreaMetric }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const { chartPoints, trendPoints } = useMemo(() => {
    const chartPoints = dataProp
      .filter((d) => d.y !== "Unknown" && !isNaN(Number(d.y)))
      .map((d) => ({
        x: Number(d.x),
        y: Number(d.y),
        date: d.date ?? null,
      }));

    const trendPoints = trendInfo?.start?.y != null && trendInfo?.end?.y != null ? [{ ...trendInfo.start }, { ...trendInfo.end }] : [];

    return { chartPoints, trendPoints };
  }, [dataProp, trendInfo]);

  const trendStatus = surfaceAreaMetric?.status;
  const trendColor = trendStatus === "Decreasing" ? "#15b471" : trendStatus === "Increasing" ? "#ff4500" : "#ff7300";

  const mcsdBandYMin = mcsdAnalysis?.y_start;
  const mcsdBandYMax = mcsdAnalysis?.y_end;

  const allYValues = chartPoints
    .map((p) => p.y)
    .concat(trendPoints.map((p) => p.y))
    .filter((v) => v != null && !isNaN(v));

  const dataYMax = Math.max(...allYValues);
  const dataYMin = Math.min(...allYValues);

  const yAxisMax = Math.ceil(dataYMax + 2);

  const yAxisMin = Math.max(0, Math.floor(Math.min(dataYMin, mcsdBandYMin ?? dataYMin) - 2));

  const xValues = chartPoints.map((p) => p.x);
  const xAxisMin = 0;
  const rawMax = Math.ceil(Math.max(...xValues));
  const xAxisMax = rawMax === 0 ? 1 : rawMax;
  const dataFloor = Math.floor(Math.min(...xValues));
  const xTickValues = [...new Set([0, dataFloor, xAxisMax])].sort((a, b) => a - b);

  const hasZeroX = xValues.some((x) => x === 0);

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: "scatter",
      plugins: [mcsdBandPlugin, valueLabelsPlugin],
      data: {
        datasets: [
          {
            label: "Surface Area",
            data: chartPoints,
            borderColor: "#387ac4",
            borderWidth: 1.5,
            pointBackgroundColor: "#387ac4",
            pointBorderColor: "rgba(56,122,196,0.35)",
            pointBorderWidth: 4,
            pointRadius: 3,
            pointHoverRadius: 4,
            showLine: true,
            spanGaps: true,
          },
          {
            label: "Trend",
            data: trendPoints,
            borderColor: trendColor,
            borderWidth: 1.5,
            borderDash: [5, 5],
            pointRadius: 0,
            showLine: true,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 30, bottom: 20, left: 20, right: 20 },
        },
        animation: { duration: 700, easing: "easeInOutQuart" },
        plugins: {
          mcsdBand: { bandYMin: mcsdBandYMin, bandYMax: mcsdBandYMax },
          valueLabels: { rows: chartPoints },
          legend: { display: false },
          tooltip: {
            backgroundColor: "#fff",
            borderColor: "#e0e0e0",
            borderWidth: 1,
            titleColor: "#555",
            bodyColor: "#222",
            padding: 10,
            callbacks: {
              title: (tooltipItems) => `Week ${tooltipItems[0].parsed.x}`,
              label: (ctx) => {
                if (ctx.datasetIndex !== 0) return `Trend: ${ctx.parsed.y} cm²`;
                const pt = chartPoints[ctx.dataIndex];
                return [`Area: ${ctx.parsed.y} cm²`, pt?.date ? `Date: ${pt.date}` : null].filter(Boolean);
              },
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            min: xAxisMin,
            max: xAxisMax,
            offset: hasZeroX,
            grid: { color: "#e8e8e8", lineWidth: 1 },
            afterBuildTicks: (axis) => {
              axis.ticks = xTickValues.map((v) => ({ value: v }));
            },
            ticks: {
              color: "#888",
              font: { size: 11 },
              maxRotation: 0,
              callback: (value) => String(value),
            },
            title: { display: true, text: "Weeks", color: "#666", font: { size: 13 } },
            border: { color: "#ccc" },
          },
          y: {
            min: yAxisMin,
            max: yAxisMax,
            grid: { color: "#e8e8e8", lineWidth: 1 },
            ticks: { color: "#888", font: { size: 11 }, stepSize: 1 },
            title: {
              display: true,
              text: "Surface Area (cm²)",
              color: "#666",
              font: { size: 13 },
            },
            border: { color: "#ccc" },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartPoints, trendPoints, mcsdBandYMin, mcsdBandYMax, yAxisMin, yAxisMax]);

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartHeader}>
        <p className={styles.chartTitle}>Wound Healing Trajectory</p>

        <div className={styles.legendRow}>
          <span className={styles.legendItem}>
            <span className={styles.legendLineSolid} />
            Actual
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendLineDash} style={{ borderTop: `2px dashed ${trendColor}` }} />
            Trend
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendBandMcsd} />
            MCSD Zone
          </span>
        </div>
      </div>

      <div className={styles.canvasContainer}>
        <canvas ref={canvasRef} role="img" aria-label="Wound surface area healing trajectory over time" />
      </div>
    </div>
  );
};

export default HealingTrajectoryChart;
